import {branch as branchHOC, renderComponent, withStateHandlers} from 'recompose'

import compose from 'lodash/flowRight'
import type * as React from 'react'
import {connect} from 'react-redux'

import {EntityProvider} from '../../../../../contexts/entity'
import type {State} from '../../../../../reducers/types'
import {getOverviewProject, getProject} from '../../../../../selectors'
import {stringifyId} from '../../../../../types'
import type {BuildTypeId, ProjectId} from '../../../../../types'
import Loader from '../../../../common/Loader/Loader'
import ShowAllBuildTypes from '../../../../common/NestedSubprojectsAndBuildTypes/ShowAllBuildTypes/ShowAllBuildTypes'
import {withVisibility} from '../../../../common/Visible/Visible.hocs'
import {useHint} from '../../../../packages/Hints/Hints.hooks'

import {BuildTypesInfoTableContainer} from './BuildTypeInfoPanel/BuildTypeInfoPanel'
import BuildTypeInfo from './BuildTypeInfoPanel/BuildTypeInfoPanel.container'
import {buildTypeInfoPanelHint} from './BuildTypeInfoPanel/BuildTypeInfoPanel.hint'

type OuterProps = {
  projectId: ProjectId
  onlyFavorites: boolean
  showAll?: boolean
}
type OwnProps = OuterProps & {
  showAll: boolean
  isVisible: boolean
  handleShowAllClick: () => unknown
}
type StateProps = {
  overviewBuildTypeIds: ReadonlyArray<BuildTypeId>
  buildTypeIds: ReadonlyArray<BuildTypeId>
  loading: boolean
}
type Props = OwnProps & StateProps
const emptyArray: never[] = []

const mapStateToProps = (state: State, {projectId}: OwnProps): StateProps => {
  const overviewProject = getOverviewProject(state, projectId)
  const project = getProject(state, projectId)
  return {
    overviewBuildTypeIds: overviewProject?.buildTypes?.buildType ?? emptyArray,
    buildTypeIds: project?.buildTypes?.buildType ?? emptyArray,
    loading: project == null && overviewProject == null,
  }
}

export default compose(
  withVisibility(false),
  withStateHandlers(
    ({showAll = false}: {showAll: boolean}) => ({
      showAll,
    }),
    {
      handleShowAllClick: () => () => ({
        showAll: true,
      }),
    },
  ),
  connect(mapStateToProps, {}),
  branchHOC(
    (props: StateProps) => props.loading,
    renderComponent(() => <Loader />),
  ),
)(
  ({
    overviewBuildTypeIds,
    buildTypeIds,
    projectId,
    showAll,
    handleShowAllClick,
    onlyFavorites,
    isVisible,
  }: Props) => {
    useHint(isVisible && buildTypeInfoPanelHint)
    const buildTypesCount = buildTypeIds.length
    const overviewBuildTypesCount = overviewBuildTypeIds.length
    const showOnlyFavorites = onlyFavorites || (overviewBuildTypesCount > 0 && !showAll)
    const sortedBuildTypeIds = showOnlyFavorites
      ? overviewBuildTypeIds
      : [...overviewBuildTypeIds, ...buildTypeIds.filter(id => !overviewBuildTypeIds.includes(id))]
    return (
      <>
        <BuildTypesInfoTableContainer childCount={sortedBuildTypeIds.length}>
          {sortedBuildTypeIds.map(id => (
            <EntityProvider key={stringifyId(id)} buildTypeId={id}>
              <BuildTypeInfo buildTypeId={id} projectId={projectId} />
            </EntityProvider>
          ))}
        </BuildTypesInfoTableContainer>
        {!onlyFavorites && showOnlyFavorites && buildTypesCount > overviewBuildTypesCount && (
          <ShowAllBuildTypes onClick={handleShowAllClick} count={buildTypesCount} />
        )}
      </>
    )
  },
) as React.ComponentType<OuterProps>
